<template>
    <div
        class="autosuggest absolute left-0 z-10 w-96 origin-top-right mt-2 border border-slate-200 shadow-sm rounded-lg"
    >
        <div class="bg-white rounded-lg">
            <div
                class="flex px-4 py-3 space-x-2 border-b items-center border-slate-200"
            >
                <div
                    class="pointer-events-none inset-y-0 flex items-center text-slate-800"
                >
                    <i class="fa-solid fa-magnifying-glass"></i>
                </div>
                <label for="property_name_search" class="sr-only">
                    Property Name Search
                </label>
                <input
                    type="text"
                    id="property_name_search"
                    class="text-sm text-slate-800 w-full p-0 border-none placeholder:text-slate-500 outline-none focus:outline-none focus:ring-0 active:outline-none"
                    @keydown.enter.prevent="enter"
                    @input="change"
                    name="query_input"
                    v-show="queryInputActive"
                    v-model="computedQuery"
                    ref="searchinput"
                    :required="requireInput"
                    autocomplete="off"
                    v-debounce="debounceTime"
                    :placeholder="placeholder"
                />
                <input
                    type="hidden"
                    :name="inputName"
                    v-model="itemValue"
                    :required="requireInput"
                />
                <ClearInputButton
                    v-show="computedQuery && computedQuery.length"
                    @clear="clearSearch"
                    class="absolute right-2"
                />
            </div>
            <div
                class="px-4 py-2 space-y-5 grow shrink basis-0 flex-col justify-start items-start flex h-48 p-2 bg-white rounded-lg overflow-scroll"
                style="transform: translate3d(0, 0, 200px)"
            >
                <div
                    class="relative flex items-center"
                    v-for="option in filteredAlerts.checked"
                    :key="option"
                >
                    <div class="flex h-6 items-center">
                        <input
                            :id="option.id"
                            :name="option.name"
                            type="checkbox"
                            class="w-4 h-4 bg-white rounded border border-slate-300 text-orange-600 focus:ring-0 focus:ouline-none"
                            :checked="selectedOptions.includes(option.id)"
                            @change="toggleSelect(option)"
                        />
                    </div>
                    <div class="ml-3 text-sm leading-6" :value="option.value">
                        <label :for="option.id">
                            <p
                                class="text-slate-800 text-sm font-normal leading-tight"
                            >
                                {{ option.name }}
                            </p>
                        </label>
                    </div>
                </div>
                <div
                    class="relative flex items-center"
                    v-for="option in filteredAlerts.unchecked"
                    :key="option"
                >
                    <div class="flex h-6 items-center">
                        <input
                            :id="option.id"
                            :name="option.name"
                            type="checkbox"
                            class="w-4 h-4 bg-white rounded border border-slate-300 text-orange-600 focus:ring-0 focus:ouline-none"
                            :checked="selectedOptions.includes(option.id)"
                            @change="toggleSelect(option)"
                        />
                    </div>
                    <div class="ml-3 text-sm leading-6" :value="option.value">
                        <label :for="option.id">
                            <p
                                class="text-slate-800 text-sm font-normal leading-tight"
                            >
                                {{ option.name }}
                            </p>
                        </label>
                    </div>
                </div>
                <div
                    class="relative flex items-start p-3 bg-white text-slate-800 text-sm leading-tight"
                    v-show="noResultsFound"
                >
                    No results found, try adjusting your search
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import { useHotelsStore } from '@/store/hotels';
import ClearInputButton from '@/components/ui/clear-input-button/clear-input-button.vue';

export default {
    name: 'AutoSuggestWithCheckboxes',
    props: {
        url: {
            type: String,
            required: true,
        },
        inputName: {
            type: String,
            default: 'search',
        },
        resultsArrayKey: {
            type: String,
            default: 'items',
        },
        requireInput: {
            type: Boolean,
            default: true,
        },
        requestMethod: {
            type: String,
            default: 'post',
        },
        placeholder: {
            type: String,
            required: false,
        },
        preloadData: {
            type: Boolean,
            default: false,
        },
        preloadedItems: {
            type: Array,
            default: () => [],
        },
        preSelectedOptions: {
            type: Array,
            default: () => [],
        },
    },
    components: {
        ClearInputButton,
    },
    data: () => ({
        itemValue: '',
        itemName: '',
        items: [],
        query: '',
        queryInputActive: true,
        queryStartLength: 2,
        scrollAmount: 40,
        selectedOption: null,
        options: [],
        selectedOptions: [],
    }),
    watch: {
        query(val) {
            if (val.length < this.queryStartLength) {
                this.items = [];
            }
        },
    },
    computed: {
        ...mapState(useHotelsStore, ['autoSuggestQuery']),
        computedQuery: {
            get() {
                return this.autoSuggestQuery
                    ? this.autoSuggestQuery
                    : this.query;
            },
            set(val) {
                this.query = val;
            },
        },
        debounceTime() {
            return this.preloadData ? 100 : 600;
        },
        computedOptions() {
            let options = [];

            if (this.preloadData) {
                options = options.concat(this.preloadedItems);
            } else {
                options = options.concat(this.items);
            }

            return options;
        },
        noResultsFound() {
            return this.filteredAlerts.length === 0;
        },
        filteredAlerts() {
            const unchecked = this.computedOptions.filter((item) => {
                let propertyNameFilter = true;
                if (this.query && this.query.length > 1) {
                    propertyNameFilter = item.name
                        .toLowerCase()
                        .includes(this.query);
                }
                return (
                    propertyNameFilter &&
                    !this.selectedOptions.includes(item.id)
                );
            });

            const checked = this.computedOptions.filter((item) => {
                let propertyNameFilter = true;
                if (this.query && this.query.length > 1) {
                    propertyNameFilter = item.name
                        .toLowerCase()
                        .includes(this.query);
                }
                return (
                    propertyNameFilter && this.selectedOptions.includes(item.id)
                );
            });

            return { checked: checked, unchecked: unchecked };
        },
    },
    mounted() {
        if (this.preSelectedOptions?.length) {
            this.selectedOptions.push(...this.preSelectedOptions);
        }
        if (!this.preloadData) {
            this.getItems();
        }

        setTimeout(() => {
            this.$refs.searchinput.focus();
        }, 200);
    },
    methods: {
        ...mapActions(useHotelsStore, [
            'setPropertyName',
            'setAutosuggestQuery',
        ]),
        async getItems() {
            try {
                const resp = await ApiService.apiCall(
                    `${this.url}${this.query}`,
                    this.requestMethod
                );
                const resultsArray = resp.data[this.resultsArrayKey];
                if (resultsArray.length > 0) {
                    this.items = resultsArray;
                } else {
                    throw new Error(
                        `No results found for search term ${this.query}`
                    );
                }
            } catch (error) {
                console.error(error);
                this.$root.handleHttpStatus(error);
            }
        },
        toggleSelect(option) {
            const index = this.selectedOptions.indexOf(option.id);
            if (index > -1) {
                this.selectedOptions.splice(index, 1);
            } else {
                this.selectedOptions.push(option.id);
            }
            this.$emit('handleCheck', option);
        },
        change(e) {
            if (this.preloadData) {
                //const query = e.target.value.toLowerCase();
                this.setAutosuggestQuery(this.query.toLowerCase());
                // this.getItems();
                //this.setPropertyName(query);
            }
            if (!this.preloadData) {
                if (e.target.value.length >= this.queryStartLength) {
                    this.query = e.target.value;
                    this.getItems();
                }
                if (!this.query && this.noResultsFound) {
                    this.noResultsFound = false;
                }
                if (!e.target.value.length) {
                    this.getItems();
                }
            }
        },
        resetActiveItem() {
            this.computedOptions.forEach((item) => {
                item.active = false;
            });
        },
        enter() {
            this.getItems();
        },
        clearSearch() {
            this.query = '';
            this.setAutosuggestQuery(null);
            this.items = [];
            this.resetActiveItem();
            this.setPropertyName(null);
            this.getItems();
        },
    },
};
</script>
